import type { FC } from 'react';
import { useEffect } from 'react';
import { z } from 'zod';
import type { NetworkPoint } from 'src/apis/monolith/types';
import { Direction } from 'src/components/data-display/direction';
import { SearchableMultiSelect } from 'src/components/form/select/searchable-multi-select';
import { formOptionSchema } from 'src/components/form/zod-schemas';
import { useAxios } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';

const networkPointDirectionMetaSchema = z.object({
  marketArea: z.string(),
  tsoShortName: z.string(),
});

const networkPointMetaSchema = z.union([
  z.object({
    exit: networkPointDirectionMetaSchema,
    entry: networkPointDirectionMetaSchema,
  }),
  z.object({
    exit: networkPointDirectionMetaSchema,
    entry: z.undefined().optional(),
  }),
  z.object({
    exit: z.undefined().optional(),
    entry: networkPointDirectionMetaSchema,
  }),
]);

export const ownNetworkPointSchema =
  networkPointMetaSchema.and(formOptionSchema);

const url = `${PRISMA_CONFIG.monolithApiUrl}/networkPoints/findNetworkPoints`;

type Props = {
  label: string;
  allowBundledNwps?: boolean;
};

type NetworkPointOption = z.output<typeof ownNetworkPointSchema>;

export const OwnNetworkPointSearchableMultiSelect: FC<Props> = ({
  label,
  allowBundledNwps = false,
}) => {
  const searchRequest = useAxios(
    (axios, baseConfig) =>
      axios.request<{ data: NetworkPoint[] }>({
        ...baseConfig,
        url,
        params: {
          all: false,
          ownTsoOnly: true,
          showOnlyInactivePoints: false,
          bundled: allowBundledNwps,
          sortAscending: true,
          sortColumn: 'name',
          pageSize: 999,
          start: 0,
        },
      }),
    { neededOnPageLoad: false }
  );

  const networkPoints = searchRequest.response?.data.data.map(
    (networkPoint) => ({
      value: networkPoint.networkPointUuid,
      label: networkPoint.name,
      hint: (
        <>
          {networkPoint.bundled
            ? `${networkPoint.tso0ShortName} / ${networkPoint.tso1ShortName}`
            : networkPoint.tso0ShortName}{' '}
          <Direction networkPoint={networkPoint} />
        </>
      ),
      ...(networkPoint.bundled
        ? {
            exit: {
              marketArea: networkPoint.marketArea0,
              tsoShortName: networkPoint.tso0ShortName,
            },
            entry: {
              marketArea: networkPoint.marketArea1,
              tsoShortName: networkPoint.tso1ShortName,
            },
          }
        : networkPoint.direction === 'ENTRY'
          ? {
              entry: {
                marketArea: networkPoint.marketArea0,
                tsoShortName: networkPoint.tso0ShortName,
              },
            }
          : {
              exit: {
                marketArea: networkPoint.marketArea0,
                tsoShortName: networkPoint.tso0ShortName,
              },
            }),
    })
  ) satisfies NetworkPointOption[] | undefined;

  const search = useClientSearch(networkPoints, (networkPoint, search) =>
    networkPoint.label.toLowerCase().match(search)
  );

  // on the very first search input we try to fetch the initial list
  useEffect(() => {
    if (!search.searchValue || searchRequest.response) return;
    searchRequest.execute();
    // oxlint-disable-next-line exhaustive-deps
  }, [search.searchValue, searchRequest.response]);

  return (
    <SearchableMultiSelect
      label={label}
      name="networkPointId"
      {...search}
      stacked
      renderOptionHint={(option) => <OptionHint option={option} />}
      renderSelectedOptionTooltip={(option) => (
        <SelectedValueTooltip option={option} />
      )}
    />
  );
};

const OptionHint: FC<{
  option: NetworkPointOption;
}> = ({ option }) => {
  if (option.entry && option.exit) {
    return (
      <>
        {option.exit.tsoShortName} / {option.entry.tsoShortName}{' '}
        <Direction networkPoint={option} />
      </>
    );
  } else if (option.entry) {
    return (
      <>
        {option.entry.tsoShortName} <Direction networkPoint={option} />
      </>
    );
  } else {
    return (
      <>
        {option.exit.tsoShortName} <Direction networkPoint={option} />
      </>
    );
  }
};

const SelectedValueTooltip: FC<{
  option: NetworkPointOption;
}> = ({ option }) => {
  if (option.entry && option.exit) {
    return (
      <>
        <strong>Exit</strong>: {option.exit.tsoShortName}
        <br />
        <strong>Entry</strong>: {option.entry.tsoShortName}
      </>
    );
  } else if (option.entry) {
    return (
      <>
        <strong>Entry</strong>: {option.entry.tsoShortName}
      </>
    );
  } else {
    return (
      <>
        <strong>Exit</strong>: {option.exit.tsoShortName}
      </>
    );
  }
};
